import { useState, useRef, useEffect } from "react";
import {
  Globe,
  Coins,
  Bot,
  Link2,
  Sparkles,
  Check,
  Loader2,
  Clock,
  Info,
  ExternalLink,
  Calendar,
} from "lucide-react";

const categories = [
  {
    id: "platform",
    title: "Platform",
    icon: Globe,
    color: "from-purple-500 to-pink-500",
    subcategories: [
      {
        name: "General",
        items: [
          { name: "Multi-wallet Support", status: "live", date: "Jul 5, 2025", desc: "Connect with Phantom, Solflare, Backpack, Bitget, OKX, Bybit, Coinbase, Brave & more" },
          { name: "Gas-free Transactions", status: "live", date: "Jun 25, 2025", desc: "No SOL needed in your wallet — we cover all transaction fees" },
          { name: "Swap Boost", status: "live", date: "Jul 3, 2025", desc: "Turn swap on to get 30% lower fees and invest 30% of your SOL rewards into $RYS" },
          { name: "Fee Matching (down to 2.5%)", status: "live", date: "Aug 8, 2025", desc: "Used a competitor? Enter their domain in our FAQ modal and get even lower fees than you had there" },
          { name: "Referral System (35%)", status: "live", date: "Jul 21, 2025", desc: "Earn 35% of fees from users you refer — highest in the industry" },
          { name: "Old 2022 SPL Tokens Support", status: "live", date: "Aug 22, 2025", desc: "Close and recover SOL from legacy 2022 SPL token accounts" },
          { name: "Smart Error Handling", status: "live", date: "Jul 4, 2025", desc: "Custom error messages for all Solana wallet and transaction issues to guide users" },
          { name: "Platform Re-design", status: "live", date: "Dec 2, 2025", desc: "Fresh modern UI/UX overhaul for better user experience" },
          { name: "A/B/C Testing", status: "planned", desc: "Optimize website for best conversion rates" },
          { name: "XP/Level System (Fee Reduction)", status: "planned", desc: "Earn XP by using the platform and RYS Contest, level up to unlock lower fees" },
          { name: "Partnerships & Collabs", status: "planned", desc: "Strategic partnerships with other Solana projects and sponsors" },
        ],
      },
      {
        name: "Burning",
        items: [
          { name: "Token Burning", status: "live", date: "Jun 25, 2025", desc: "Burn unwanted SPL tokens to close accounts and recover SOL" },
          { name: "Safety Burn", status: "live", date: "Jun 27, 2025", desc: "Burned by mistake? We refund the equivalent SOL value in most cases via support" },
          { name: "Optimized Token Loading", status: "live", date: "Oct 30, 2025", desc: "Huge optimization — from 10s+ to less than 2s loading time via new data pipeline" },
        ],
      },
      {
        name: "API",
        items: [
          { name: "Documentation Portal", status: "live", date: "Dec 16, 2025", desc: "Full API documentation with guides, examples, and interactive endpoints", link: "https://refundyoursol.com/docs" },
          { name: "Smart Batch Optimizer", status: "live", date: "Dec 18, 2025", desc: "useOptimizer=true for cheapest possible wallet cleaning — smart TX batching for market makers & holder tools", link: "https://refundyoursol.com/docs#batch-optimizer" },
          { name: "Multi-wallet API (/docs)", status: "live", date: "Nov 3, 2025", desc: "Clean up to 20 wallets programmatically via API" },
          { name: "Closing API", status: "live", date: "Nov 3, 2025", desc: "Close token accounts via API for automated workflows" },
          { name: "Burning API", status: "live", date: "Nov 3, 2025", desc: "Burn tokens programmatically with API integration" },
          { name: "Safety Burn API", status: "live", date: "Nov 3, 2025", desc: "Safety burn protection available via API calls" },
          { name: "Complete Clean API (Refund All SOL)", status: "live", date: "Nov 3, 2025", desc: "Fully clean wallets and send all recovered SOL to specified address" },
          { name: "WSOL/USDC Refund Option", status: "live", date: "Dec 5, 2025", desc: "Choose WSOL or USDC as your preferred refund token via API" },
          { name: "Send Tokens to Different Wallet", status: "planned", desc: "Transfer tokens to another wallet instead of burning" },
        ],
      },
    ],
  },
  {
    id: "staking",
    title: "Staking",
    icon: Coins,
    color: "from-pink-500 to-orange-400",
    items: [
      { name: "Staking Platform (1/3/6 months)", status: "live", date: "Aug 16, 2025", desc: "Lock $RYS for 1, 3, or 6 months to earn rewards" },
      { name: "1-Week Staking Option", status: "live", date: "Feb 9, 2026", desc: "New 1-week lock option (shown as “1 Week” instead of “0 month” in stake notifications)" },
      { name: "Daily Claimable Rewards", status: "live", date: "Aug 16, 2025", desc: "Claim your staking rewards every day" },
      { name: "Auto-compound", status: "live", date: "Sep 3, 2025", desc: "Automatically reinvest rewards to boost your APY by ~30%" },
      { name: "Revenue Sharing (50%)", status: "live", date: "Aug 16, 2025", desc: "50% of all platform revenue goes directly to stakers" },
      { name: "Early Unlock (-35% Penalty)", status: "live", date: "Feb 9, 2026", desc: "Unlock your stake early with a 35% $RYS penalty (penalty goes to burn wallet)" },
    ],
  },
  {
    id: "bot",
    title: "RYS Bot",
    icon: Bot,
    color: "from-orange-400 to-yellow-400",
    subcategories: [
      {
        name: "Wallet Cleaning",
        items: [
          { name: "Wallet Checking", status: "live", date: "Jun 29, 2025", desc: "Scan your wallet to see closeable token accounts and potential SOL recovery" },
          { name: "Wallet Import (All PK Formats)", status: "live", date: "Jul 30, 2025", desc: "Import wallets using private key, seed phrase, or key array" },
          { name: "Auto Cleanup (Daily/3-Day/Weekly)", status: "live", date: "Jul 30, 2025", desc: "Schedule automatic wallet cleanup on your preferred interval" },
          { name: "Manual Cleanup (Close Accounts)", status: "live", date: "Jul 30, 2025", desc: "One-click button to close all token accounts and recover SOL" },
          { name: "Cleanup Stats (Accounts & SOL)", status: "live", date: "Aug 8, 2025", desc: "Track how many accounts you've closed and SOL recovered" },
          { name: "Burn Token Selection", status: "planned", desc: "Choose which specific tokens to burn instead of all" },
        ],
      },
      {
        name: "Group Bot",
        items: [
          { name: "Buy Bot", status: "live", date: "Jul 22, 2025", desc: "Real-time buy alerts for your token in Telegram groups" },
          { name: "Holders & Whales System", status: "live", date: "Jun 29, 2025", desc: "Create whale groups, verify wallets & holdings, auto-invite eligible users, auto-kick sellers" },
          { name: "Welcome & Guard Bot", status: "live", date: "Jul 6, 2025", desc: "Welcome messages + protect groups from spam, scams, and unwanted content" },
          { name: "Raid Bot", status: "live", date: "Sep 13, 2025", desc: "Coordinate community raids and engagement campaigns" },
          { name: "X Tracker", status: "live", date: "Dec 2025", desc: "Track and display X/Twitter activity in your group" },
          { name: "Burn Tracker", status: "live", date: "Dec 2025", desc: "Monitor and announce token burns in real-time" },
          { name: "DEXScreener Rank Tracker", status: "live", date: "Feb 2026", desc: "Track and display DEXScreener rank in your group" },
          { name: "Analytics Bot", status: "planned", desc: "Track token stats, holders, and market data" },
          { name: "Anti-Rug Detector", status: "planned", desc: "Scan tokens for potential rug pull risks" },
          { name: "Bundle Detector", status: "planned", desc: "Detect bundled transactions and insider activity" },
          { name: "Promotional Messages", status: "planned", desc: "Send promotional messages to group members from time to time" },
        ],
      },
      {
        name: "Group Management",
        items: [
          { name: "Mention All Users", status: "live", date: "Jul 15, 2025", desc: "Clean mention system without showing individual mentions, with reply support" },
          { name: "Custom Commands", status: "live", date: "Jul 15, 2025", desc: "Create, modify, enable/disable commands and trigger messages" },
          { name: "Bot Blocking", status: "live", date: "Jul 15, 2025", desc: "Clean up chat from other bots' annoying messages (keep them for ranking leaderboards)" },
          { name: "Report & Vote System", status: "live", date: "Jul 15, 2025", desc: "No mods needed — community democracy decides who gets banned/muted" },
        ],
      },
      {
        name: "Advanced Guard (AI)",
        items: [
          { name: "AI Spam Detection", status: "progress", desc: "Detect and remove nudity promoters, self-promoters, call channel shillers" },
          { name: "Link Detection & Warnings", status: "progress", desc: "Detect links, give warnings, auto-ban after enough violations with full stats" },
          { name: "Scan Deleted Accounts", status: "live", date: "Dec 18, 2025", desc: "Detect and recover from previously deleted token accounts" },
          { name: "Known Scammer Detection", status: "planned", desc: "Automatically detect and block known scammers from database" },
          { name: "Scam Database", status: "planned", desc: "Curated list of known scam YouTubers, KOLs, and Telegram channels" },
        ],
      },
      {
        name: "Raiding System",
        items: [
          { name: "Hire Raiders System", status: "live", desc: "Devs hire raiders with full action tracking and quality scoring" },
          { name: "Smart Quality Check", status: "live", desc: "Checks comment age and post age — punishes raiding old posts for quality control" },
          { name: "Automatic USDC Payouts", status: "live", desc: "Raiders paid automatically based on score and shift completion" },
          { name: "Raid Manager Dashboard", status: "live", desc: "Manage raiders, replace them, change shifts, monitor performance" },
          { name: "Raid Payout Calculator", status: "live", desc: "Calculate expected payouts based on raid amount and quality" },
          { name: "Manager Payouts", status: "live", desc: "Separate payout system for raid managers and leaders" },
          { name: "Customizable Cuts", status: "live", desc: "Raid managers can customize payment splits for their team members" },
          { name: "Custom Raid Leader Roles", status: "live", desc: "Raid manager can assign a person to post raids on their behalf" },
        ],
      },
      {
        name: "RYS Contest",
        items: [
          { name: "Community Score System", status: "live", date: "Feb 19, 2026", desc: "Overall score combining all task categories — determines rewards and rankings" },
          { name: "Leaderboard & Rankings", status: "live", desc: "Live leaderboard showing top performers across all categories" },
          { name: "Automatic Rewards Distribution", status: "live", date: "Feb 19, 2026", desc: "Rewards paid automatically based on score and ranking" },
        ],
      },
      {
        name: "Contest Tasks",
        items: [
          { name: "Raiding Tasks", status: "live", date: "Feb 19, 2026", desc: "Earn points for raiding posts, quality engagement scoring" },
          { name: "X/Twitter Tasks", status: "live", date: "Feb 19, 2026", desc: "Post about RYS, retweet, like, comment on official posts" },
          { name: "X Post", status: "live", date: "Feb 19, 2026", desc: "Create posts about RYS on X/Twitter with required hashtags" },
          { name: "X Follow", status: "live", desc: "Follow @refundyoursol on X/Twitter" },
          { name: "X Profile Picture", status: "live", date: "Feb 19, 2026", desc: "Set RYS-themed profile picture on X/Twitter" },
          { name: "X Emoji Name", status: "live", desc: "Add RYS emoji or $RYS to your X/Twitter display name" },
          { name: "X Bio Link", status: "live", desc: "Add RefundYourSOL link to your X/Twitter bio or website field" },
          { name: "Reddit Post", status: "live", date: "Feb 19, 2026", desc: "Create posts about RYS on Reddit (respecting subreddit rules)" },
          { name: "Chat Activity", status: "live", desc: "Be active in Telegram, answer questions, help newcomers" },
          { name: "Telegram Chat Streak", status: "live", desc: "Maintain daily chat streaks in Telegram groups" },
          { name: "Discord Activity", status: "live", date: "Feb 19, 2026", desc: "Participate in Discord, help others, engage in discussions" },
          { name: "Discord Chat Streak", status: "live", desc: "Maintain daily chat streaks in Discord server" },
          { name: "Discord Name Tag", status: "live", desc: "Add RYS Discord server tag to your Discord name" },
          { name: "Discord Bio Link", status: "live", desc: "Add RefundYourSOL link to your Discord bio" },
          { name: "Join Discord", status: "live", desc: "Join the official RYS Discord server" },
          { name: "Token Balance Bonus", status: "live", date: "Feb 19, 2026", desc: "Bonus points based on RYS holdings" },
          { name: "Referral Points", status: "live", date: "Feb 19, 2026", desc: "Earn points for each user or group you refer" },
          { name: "Discord Invite Referrals", status: "live", date: "Feb 19, 2026", desc: "Invite people to RYS Discord using tracked invite links" },
          { name: "Group Boosting", status: "live", date: "Feb 19, 2026", desc: "Boost Telegram group or Discord server" },
          { name: "Telegram Group Boost", status: "live", date: "Feb 19, 2026", desc: "Boost the official RYS Telegram group" },
          { name: "Discord Server Boost", status: "live", date: "Feb 19, 2026", desc: "Boost the official RYS Discord server" },
          { name: "DEXScreener Voting", status: "live", date: "Feb 19, 2026", desc: "Vote for RYS on DEXScreener daily" },
          { name: "Platform Reviews", status: "live", date: "Feb 19, 2026", desc: "Write reviews on Trustpilot, ScamAdviser, GrindinSoft" },
          { name: "Trustpilot Review", status: "live", desc: "Leave a review on Trustpilot" },
          { name: "Gridinsoft Review", status: "live", desc: "Leave a review on Gridinsoft" },
          { name: "X Community Join", status: "live", date: "Feb 19, 2026", desc: "Join and engage in the official X community" },
          { name: "Connect X Account", status: "live", desc: "Link your X account to the RYS bot for verification" },
          { name: "RYS Name/Emoji", status: "live", desc: "Add $RYS or RYS custom emoji to your Telegram display name" },
          { name: "Add RYS Link in Bio", status: "live", desc: "Add RefundYourSOL link to your Telegram bio" },
          { name: "Telegram Profile Picture", status: "live", desc: "Set RYS-themed profile picture on Telegram" },
          { name: "Join RYS Channel", status: "live", desc: "Join the official RYS Telegram channel" },
          { name: "Daily Check-in", status: "live", desc: "Daily check-in via the bot to earn points" },
          { name: "Closing/Burning Points", status: "live", desc: "Earn points for using the platform to close/burn accounts" },
          { name: "Wallet Auto-Clean", status: "live", desc: "Earn daily points for having wallet connected, bonus when cleaned" },
          { name: "Link Wallet", status: "live", desc: "Link your Solana wallet to the bot for verification" },
          { name: "Refer RYS Bot to Groups", status: "live", date: "Feb 19, 2026", desc: "Bring new Telegram groups to use RYS bot" },
        ],
      },
    ],
  },
  {
    id: "listings",
    title: "Listings",
    icon: Link2,
    color: "from-yellow-400 to-green-400",
    items: [
      { name: "CoinMarketCap", status: "live", date: "Jul 3, 2025", desc: "Listed and trackable on CoinMarketCap", link: "https://coinmarketcap.com/currencies/refundyoursol/" },
      { name: "CoinGecko", status: "live", date: "Jul 3, 2025", desc: "Listed and verified on CoinGecko", link: "https://www.coingecko.com/en/coins/refundyoursol" },
      { name: "DEXScreener", status: "live", date: "Jun 25, 2025", desc: "Full profile and trading data on DEXScreener", link: "https://dexscreener.com/solana/bux9tn5doe5hcqpcmqmkykidxc8zgbk5whhkujdaabiq" },
      { name: "Jupiter (Verified)", status: "live", date: "Jul 6, 2025", desc: "Verified and tradeable on Jupiter aggregator", link: "https://jup.ag/tokens/8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump" },
      { name: "Solscan (Verified)", status: "live", date: "Jul 1, 2025", desc: "Verified token profile on Solscan explorer", link: "https://solscan.io/account/8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump" },
      { name: "Security Audit Partner", status: "live", date: "Feb 12, 2025", desc: "Partnership for trust, safety & security auditing — fast token data" },
      { name: "Solana Vibe Station", status: "live", date: "Feb 3, 2025", desc: "Partnership with Solana Vibe Station community", link: "https://discord.com/channels/1241508519190986752/1337246848154599547" },
      { name: "SolHouse Signal", status: "live", date: "May 29, 2025", desc: "Partnership with SolHouse Signal — 12K+ subscribers Solana alpha channel", link: "https://t.me/solhousesignal" },
      { name: "Farcaster dApp", status: "live", date: "Jul 15, 2025", desc: "Listed as dApp on Farcaster with official project profile", link: "https://warpcast.com/refundyoursol" },
      { name: "Phantom Wallet", status: "live", date: "Sep 2, 2025", desc: "Listed as a dApp in Phantom wallet — 115K+ transactions, 24K+ active users", link: "https://phantom.com/apps/refundyoursol" },
      { name: "Bitget Wallet", status: "live", date: "Dec 13, 2025", desc: "Listed as a dApp in Bitget Wallet", link: "https://web3.bitget.com" },
      { name: "Solana Mobile dApp (Seeker)", status: "live", date: "Dec 10, 2025", desc: "Listed on Seeker Mobile App Store — 150K+ Saga & Seeker users, 0% fees promo for Seeker wallets", link: "https://refundyoursol.com" },
      { name: "Trust Wallet", status: "planned", desc: "Submit and confirm listing via developer portal" },
      { name: "Binance Web3 Wallet", status: "planned", desc: "Apply for inclusion in the dApp directory" },
      { name: "Backpack Wallet", status: "planned", desc: "Submit project through form and finalize follow-ups" },
      { name: "OKX Wallet", status: "live", date: "Dec 14, 2025", desc: "Listed as a dApp in OKX Wallet", link: "https://www.okx.com/web3" },
      { name: "GetBlock", status: "planned", desc: "Infrastructure partnership for reliable RPC access" },
      { name: "Alchemy DeFi dApp", status: "planned", desc: "Submit RYS and secure a live profile on Alchemy" },
    ],
  },
  {
    id: "ecosystem",
    title: "Ecosystem",
    icon: Sparkles,
    color: "from-green-400 to-cyan-400",
    subcategories: [
      {
        name: "Services",
        items: [
          { name: "Trendify (Trending & Marketing)", status: "progress", desc: "Trending channel with marketing services for projects" },
          { name: "RYS Trading Bot", status: "progress", desc: "Automated trading utility for Solana tokens" },
          { name: "Portfolio Tracker", status: "progress", desc: "Track your holdings and performance across wallets" },
          { name: "Market Making Services", status: "progress", desc: "Professional market making — volume bot, makers bot (increases unique traders), holder growth tools" },
        ],
      },
      {
        name: "Community",
        items: [
          { name: "X Community", status: "live", date: "Jul 16, 2025", desc: "Official X community for RYS holders and supporters", link: "https://x.com/i/communities/1938025807126867975" },
          { name: "YouTube Channel", status: "live", date: "Sep 6, 2025", desc: "Official tutorials and updates on YouTube", link: "https://youtube.com/@refundyoursol" },
          { name: "Russian Speaking Group", status: "planned", desc: "Dedicated community group for Russian speakers" },
          { name: "Chinese Speaking Group", status: "planned", desc: "Dedicated community group for Chinese speakers" },
        ],
      },
      {
        name: "Major Platforms (Coming)",
        items: [
          { name: "RYS Whitelabel", status: "progress", desc: "Launch your own RYS-powered wallet cleaning site — full API, staking, and analytics baked in. Perfect for projects wanting their own branded experience" },
          { name: "RYS Wallet", status: "planned", desc: "Our own Solana wallet with built-in cleaning, staking, and all RYS features" },
          { name: "RYS Launchpad", status: "planned", desc: "Launch your own token with built-in tools, marketing, and community support" },
        ],
      },
    ],
  },
];

// Custom hook for intersection observer (iOS-safe)
const useIsVisible = (ref, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (options.once !== false) {
          observer.disconnect();
        }
      }
    }, { threshold: options.amount || 0.3 });
    
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, options.once, options.amount]);
  
  return isVisible;
};

const statusConfig = {
  live: {
    icon: Check,
    label: "Live",
    bg: "bg-green-500/20",
    text: "text-green-400",
    dot: "bg-green-500",
  },
  testing: {
    icon: Loader2,
    label: "Testing",
    bg: "bg-blue-500/20",
    text: "text-blue-400",
    dot: "bg-blue-500",
  },
  progress: {
    icon: Loader2,
    label: "In Progress",
    bg: "bg-yellow-500/20",
    text: "text-yellow-400",
    dot: "bg-yellow-500",
  },
  planned: {
    icon: Clock,
    label: "Planned",
    bg: "bg-gray-500/20",
    text: "text-gray-400",
    dot: "bg-gray-500",
  },
};

// Helper to flatten items from category (handles both flat items and subcategories)
const getAllItems = (category) => {
  if (category.subcategories) {
    return category.subcategories.flatMap((sub) => sub.items);
  }
  return category.items || [];
};

export default function Roadmap() {
  const [activeCategory, setActiveCategory] = useState("platform");
  const [filter, setFilter] = useState("all");

  const activeData = categories.find((c) => c.id === activeCategory);
  const allItems = getAllItems(activeData);

  const filteredItems =
    filter === "all"
      ? allItems
      : allItems.filter((item) => item.status === filter);

  const counts = {
    all: allItems.length || 0,
    live: allItems.filter((i) => i.status === "live").length || 0,
    testing: allItems.filter((i) => i.status === "testing").length || 0,
    progress: allItems.filter((i) => i.status === "progress").length || 0,
    planned: allItems.filter((i) => i.status === "planned").length || 0,
  };

  const hasSubcategories = activeData?.subcategories;

  const headingRef = useRef(null);
  const headingVisible = useIsVisible(headingRef, { once: true });
  const tabsRef = useRef(null);
  const tabsVisible = useIsVisible(tabsRef, { once: true });

  return (
    <section
      id="roadmap"
      className="relative py-24 border-t border-white/5 bg-[#0f0f15]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(126,34,206,0.15),transparent_70%)]" />
      <div className="relative max-w-5xl mx-auto px-6">
        <h3
          ref={headingRef}
          className={`text-3xl md:text-4xl font-bold mb-4 text-center gradient-text transition-all duration-600 ${
            headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
          style={{ WebkitBackfaceVisibility: 'hidden' }}
        >
          Progress Tracker
        </h3>
        <p
          className={`text-gray-400 text-center mb-12 max-w-2xl mx-auto transition-all duration-600 ${
            headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
          style={{ transitionDelay: '0.1s', WebkitBackfaceVisibility: 'hidden' }}
        >
          We ship fast and continuously. Here's what we've built, what we're working on, and what's coming next.
        </p>

        {/* Category Tabs */}
        <div
          ref={tabsRef}
          className={`flex flex-wrap justify-center gap-2 mb-8 transition-all duration-600 ${
            tabsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
          style={{ transitionDelay: '0.2s', WebkitBackfaceVisibility: 'hidden' }}
        >
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            const catItems = getAllItems(cat);
            const liveCount = catItems.filter((i) => i.status === "live").length;

            return (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setFilter("all");
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  isActive
                    ? `bg-gradient-to-r ${cat.color} text-white shadow-lg`
                    : "glass text-gray-400 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" />
                {cat.title}
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? "bg-white/20" : "bg-white/10"
                  }`}
                >
                  {liveCount}/{catItems.length}
                </span>
              </button>
            );
          })}
        </div>

        {/* Status Filter */}
        <div
          className={`flex justify-center gap-2 mb-8 transition-opacity duration-400 ${
            tabsVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '0.3s', WebkitBackfaceVisibility: 'hidden' }}
        >
          {[
            { key: "all", label: "All" },
            { key: "live", label: "Live" },
            { key: "testing", label: "Testing" },
            { key: "progress", label: "In Progress" },
            { key: "planned", label: "Planned" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                filter === f.key
                  ? "bg-purple-500/30 text-purple-300 ring-1 ring-purple-500/50"
                  : "text-gray-500 hover:text-gray-300"
              }`}
            >
              {f.label} ({counts[f.key]})
            </button>
          ))}
        </div>

        {/* Progress Bar */}
        <div
          className={`mb-8 transition-all duration-600 ${
            tabsVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '0.3s', WebkitBackfaceVisibility: 'hidden' }}
        >
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Progress</span>
            <span>
              {counts.live} of {counts.all} complete
            </span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${activeData?.color} rounded-full transition-all duration-800`}
              style={{ 
                width: `${(counts.live / counts.all) * 100}%`,
                WebkitBackfaceVisibility: 'hidden',
              }}
            />
          </div>
        </div>

        {/* Items Grid */}
        <div
          className="transition-opacity duration-200"
          style={{ WebkitBackfaceVisibility: 'hidden' }}
        >
          {hasSubcategories ? (
            // Render with subcategory headers
            <div className="space-y-8">
              {activeData.subcategories.map((sub, subIndex) => {
                const subFiltered =
                  filter === "all"
                    ? sub.items
                    : sub.items.filter((item) => item.status === filter);

                if (subFiltered.length === 0) return null;

                return (
                  <div key={sub.name}>
                    <h4 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${activeData.color}`} />
                      {sub.name}
                    </h4>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {subFiltered.map((item, i) => {
                        const status = statusConfig[item.status];
                        const StatusIcon = status.icon;

                        return (
                          <div
                            key={item.name}
                            className={`glass rounded-xl p-4 flex items-center gap-3 border group relative ${
                              item.status === "live"
                                ? "border-green-500/20"
                                : item.status === "testing"
                                ? "border-blue-500/20"
                                : item.status === "progress"
                                ? "border-yellow-500/20"
                                : "border-white/5"
                            }`}
                            style={{ WebkitBackfaceVisibility: 'hidden' }}
                          >
                            <div
                              className={`w-8 h-8 rounded-full ${status.bg} flex items-center justify-center flex-shrink-0`}
                            >
                              <StatusIcon
                                className={`w-4 h-4 ${status.text} ${
                                  item.status === "progress" || item.status === "testing" ? "animate-spin" : ""
                                }`}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white font-medium truncate">
                                {item.name}
                              </p>
                              <div className="flex items-center gap-2">
                                <p className={`text-xs ${status.text}`}>{status.label}</p>
                                {item.date && (
                                  <span className="text-[10px] text-gray-500 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {item.date}
                                  </span>
                                )}
                              </div>
                            </div>
                            {item.link && (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="w-4 h-4 text-purple-400 hover:text-purple-300 cursor-pointer" />
                              </a>
                            )}
                            {item.desc && (
                              <div className="relative">
                                <Info className="w-4 h-4 text-gray-500 hover:text-gray-300 cursor-help" />
                                <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-gray-900 border border-white/10 rounded-lg text-xs text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-xl">
                                  {item.desc}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Render flat grid
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filteredItems?.map((item, i) => {
                const status = statusConfig[item.status];
                const StatusIcon = status.icon;

                return (
                  <div
                    key={item.name}
                    className={`glass rounded-xl p-4 flex items-center gap-3 border group relative ${
                      item.status === "live"
                        ? "border-green-500/20"
                        : item.status === "testing"
                        ? "border-blue-500/20"
                        : item.status === "progress"
                        ? "border-yellow-500/20"
                        : "border-white/5"
                    }`}
                    style={{ WebkitBackfaceVisibility: 'hidden' }}
                  >
                    <div
                      className={`w-8 h-8 rounded-full ${status.bg} flex items-center justify-center flex-shrink-0`}
                    >
                      <StatusIcon
                        className={`w-4 h-4 ${status.text} ${
                          item.status === "progress" || item.status === "testing" ? "animate-spin" : ""
                        }`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium truncate">
                        {item.name}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className={`text-xs ${status.text}`}>{status.label}</p>
                        {item.date && (
                          <span className="text-[10px] text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {item.date}
                          </span>
                        )}
                      </div>
                    </div>
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4 text-purple-400 hover:text-purple-300 cursor-pointer" />
                      </a>
                    )}
                    {item.desc && (
                      <div className="relative">
                        <Info className="w-4 h-4 text-gray-500 hover:text-gray-300 cursor-help" />
                        <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-gray-900 border border-white/10 rounded-lg text-xs text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-xl">
                          {item.desc}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Stats Summary */}
        <div
          className={`mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-lg mx-auto transition-all duration-600 ${
            tabsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
          style={{ transitionDelay: '0.4s', WebkitBackfaceVisibility: 'hidden' }}
        >
          {[
            { label: "Live", count: categories.reduce((acc, c) => acc + getAllItems(c).filter((i) => i.status === "live").length, 0), color: "text-green-400" },
            { label: "Testing", count: categories.reduce((acc, c) => acc + getAllItems(c).filter((i) => i.status === "testing").length, 0), color: "text-blue-400" },
            { label: "In Progress", count: categories.reduce((acc, c) => acc + getAllItems(c).filter((i) => i.status === "progress").length, 0), color: "text-yellow-400" },
            { label: "Planned", count: categories.reduce((acc, c) => acc + getAllItems(c).filter((i) => i.status === "planned").length, 0), color: "text-gray-400" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.count}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
